import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

// SplitPane: two panes with a draggable (and keyboard-adjustable) divider. Horizontal by
// default. Size is the first pane's px width (or height), clamped to min/max, remembered
// under `storageKey` when given.

export interface SplitPaneProps {
  first: ReactNode;
  second: ReactNode;
  direction?: 'horizontal' | 'vertical';
  initial?: number;
  min?: number;
  max?: number;
  storageKey?: string;
  /** Which pane the size applies to. */
  sized?: 'first' | 'second';
  /** Hide one pane entirely (the other fills the box; no divider). The size is remembered. */
  collapse?: 'first' | 'second';
  className?: string;
}

export function SplitPane({ first, second, direction = 'horizontal', initial = 320, min = 160, max = 900, storageKey, sized = 'first', collapse, className }: SplitPaneProps) {
  const [size, setSize] = useState<number>(() => {
    if (storageKey) {
      try {
        const v = Number(localStorage.getItem(`hf.split.${storageKey}`));
        if (v) return v;
      } catch {
        /* ignore */
      }
    }
    return initial;
  });
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(`hf.split.${storageKey}`, String(size));
    } catch {
      /* ignore */
    }
  }, [size, storageKey]);

  const clamp = useCallback((n: number) => Math.max(min, Math.min(max, n)), [min, max]);

  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const pos = direction === 'horizontal' ? e.clientX - r.left : e.clientY - r.top;
    const total = direction === 'horizontal' ? r.width : r.height;
    setSize(clamp(sized === 'first' ? pos : total - pos));
  };
  const onPointerUp = () => {
    dragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  const style = direction === 'horizontal' ? { gridTemplateColumns: sized === 'first' ? `${size}px 1px minmax(0, 1fr)` : `minmax(0, 1fr) 1px ${size}px` } : { gridTemplateRows: sized === 'first' ? `${size}px 1px minmax(0, 1fr)` : `minmax(0, 1fr) 1px ${size}px` };

  // Collapsed: the remaining pane fills the box. The divider goes with it — there is nothing
  // left to drag — and the remembered size is restored when the pane comes back.
  if (collapse) return <div className={['hf-split-solo', className].filter(Boolean).join(' ')}>{collapse === 'first' ? second : first}</div>;

  return (
    <div ref={ref} className={['hf-split', direction, className].filter(Boolean).join(' ')} style={style}>
      <div className="pane">{first}</div>
      <div
        className="divider"
        role="separator"
        aria-orientation={direction === 'horizontal' ? 'vertical' : 'horizontal'}
        aria-valuenow={size}
        aria-valuemin={min}
        aria-valuemax={max}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 40 : 10;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') setSize((s) => clamp(s - step));
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') setSize((s) => clamp(s + step));
        }}
        onDoubleClick={() => setSize(initial)}
      />
      <div className="pane">{second}</div>
    </div>
  );
}
